import { supabaseClient } from 'supabase'
import { DeskQueueRow, DeskTabValue } from './deskTypes'

type DeskOrder = {
  column: keyof DeskQueueRow
  options: { ascending: boolean; nullsFirst?: boolean }
}

/**
 * The tab's ordering (D-04), shared by the list and by the next-candidate read
 * so "next" is always the row the list shows first.
 *
 * Ready, Needs a look and Update proposals: soonest start first, undated last,
 * then newest raw item. Discard pile: most recently discarded or rejected
 * first. Each ends on `application_id` (nulls last: a discarded raw item has
 * none, and it is the only row of its raw item) so ties never reshuffle
 * between pages: siblings of one raw item share raw_item_id and often a date,
 * and `.range()` over an unstable order can skip or repeat a row.
 */
export const deskTabOrder = (tab: DeskTabValue): DeskOrder[] => {
  const tieBreak: DeskOrder = {
    column: 'application_id',
    options: { ascending: true, nullsFirst: false }
  }
  if (tab === 'discard') {
    return [
      { column: 'discarded_at', options: { ascending: false } },
      { column: 'raw_item_id', options: { ascending: false } },
      tieBreak
    ]
  }
  return [
    { column: 'starting_date', options: { ascending: true, nullsFirst: false } },
    { column: 'starting_time', options: { ascending: true, nullsFirst: false } },
    { column: 'raw_item_id', options: { ascending: false } },
    tieBreak
  ]
}

export type QueryDeskQueueParams = {
  // All required: a caller that forgets one must fail to compile rather than
  // silently inherit a default it did not intend.
  tab: DeskTabValue
  page: number
  perPage: number
}

export type DeskQueuePage = {
  rows: DeskQueueRow[]
  count: number | null
  /**
   * The page asked for starts past the end of the tab (PostgREST PGRST103,
   * HTTP 416): the last row of the last page was just decided. The desk moves
   * back a page; it is not a load failure (review AD-WR-05).
   */
  outOfRange?: true
}

/**
 * One page of one desk tab. Admin-only: `desk_queue` is a security_invoker
 * view, so the base tables' admin RLS applies and a non-admin reads nothing.
 *
 * An error is thrown raw (so `mapDeskError` can read its code), never
 * swallowed into `[]`: an empty tab reads as "nothing to do", which is a
 * different and wrong statement.
 */
export const queryDeskQueue = async ({
  tab,
  page,
  perPage
}: QueryDeskQueueParams): Promise<DeskQueuePage> => {
  // `count: 'exact'` is load-bearing: PostgREST silently truncates a select at
  // 1000 rows, so the total can never be read off the fetched array. Keep it,
  // and keep the `.range()` that pairs with it. The tab badges do not use this
  // count either: they come from desk_counts() (D-03).
  let query = supabaseClient
    .from('desk_queue')
    .select('*', { count: 'exact' })
    .eq('desk_tab', tab)

  for (const { column, options } of deskTabOrder(tab)) {
    query = query.order(column, options)
  }

  const { data, count, error } = await query.range(
    (page - 1) * perPage,
    page * perPage - 1
  )

  if (error && error.code === 'PGRST103') {
    return { rows: [], count: count ?? null, outOfRange: true }
  }
  if (error) {
    throw error
  }
  return { rows: data ?? [], count: count ?? null }
}
